import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Label } from '@/types'

interface LabelState {
  allLabels: Label[]
  recentLabels: Label[]
  suggestedLabels: Label[]
  loading: boolean
  error: string | null
  searchQuery: string
}

const initialState: LabelState = {
  allLabels: [],
  recentLabels: [],
  suggestedLabels: [],
  loading: false,
  error: null,
  searchQuery: ''
}

// 異步操作：獲取所有標籤
export const fetchLabels = createAsyncThunk(
  'labels/fetchLabels',
  async (params: { search?: string; page?: number; limit?: number; includeSystem?: boolean } = {}) => {
    const { search = '', page = 1, limit = 50, includeSystem = true } = params
    const token = localStorage.getItem('auth_token')
    
    const queryParams = new URLSearchParams({
      search,
      page: page.toString(),
      limit: limit.toString(),
      includeSystem: includeSystem.toString()
    })
    
    const response = await fetch(`/api/labels?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch labels')
    }
    
    const data = await response.json()
    return data.labels
  }
)

// 異步操作：獲取最近使用的標籤
export const fetchRecentLabels = createAsyncThunk(
  'labels/fetchRecentLabels',
  async (limit: number = 10) => {
    const token = localStorage.getItem('auth_token')
    
    const response = await fetch(`/api/labels/recent?limit=${limit}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch recent labels')
    }
    
    const data = await response.json()
    return data.labels
  }
)

// 異步操作：獲取建議標籤
export const fetchSuggestedLabels = createAsyncThunk(
  'labels/fetchSuggestedLabels',
  async (params: { projectId?: string; content?: string; limit?: number }) => {
    const { projectId, content, limit = 10 } = params
    const token = localStorage.getItem('auth_token')
    
    const queryParams = new URLSearchParams({
      limit: limit.toString()
    })
    
    if (projectId) queryParams.append('projectId', projectId)
    if (content) queryParams.append('content', content)
    
    const response = await fetch(`/api/labels/suggestions?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch suggested labels')
    }
    
    const data = await response.json()
    return data.labels
  }
)

// 異步操作：建立新標籤
export const createLabel = createAsyncThunk(
  'labels/createLabel',
  async (labelData: { name: string; color: string }) => {
    const token = localStorage.getItem('auth_token')
    
    const response = await fetch('/api/labels', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(labelData)
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to create label')
    }
    
    return await response.json()
  }
)

// 異步操作：更新標籤
export const updateLabel = createAsyncThunk(
  'labels/updateLabel',
  async (params: { id: string; name?: string; color?: string }) => {
    const { id, ...updateData } = params
    const token = localStorage.getItem('auth_token')
    
    const response = await fetch(`/api/labels/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to update label')
    }
    
    return await response.json()
  }
)

// 異步操作：刪除標籤
export const deleteLabel = createAsyncThunk(
  'labels/deleteLabel',
  async (id: string) => {
    const token = localStorage.getItem('auth_token')
    
    const response = await fetch(`/api/labels/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to delete label')
    }
    
    return id
  }
)

// 異步操作：更新議題標籤
export const updateIssueLabels = createAsyncThunk(
  'labels/updateIssueLabels',
  async (params: { issueId: string; labelIds: string[] }) => {
    const { issueId, labelIds } = params
    const token = localStorage.getItem('auth_token')
    
    const response = await fetch(`/api/issues/${issueId}/labels`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ labelIds })
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to update issue labels')
    }
    
    const data = await response.json()
    return { issueId, issue: data.data.issue }
  }
)

const labelSlice = createSlice({
  name: 'labels',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    // 當議題標籤在其他地方更新時，用於同步更新 recent labels
    updateRecentLabels: (state, action: PayloadAction<Label[]>) => {
      state.recentLabels = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // 獲取所有標籤
      .addCase(fetchLabels.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.loading = false
        state.allLabels = action.payload
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch labels'
      })

      // 獲取最近使用標籤
      .addCase(fetchRecentLabels.fulfilled, (state, action) => {
        state.recentLabels = action.payload
      })

      // 獲取建議標籤
      .addCase(fetchSuggestedLabels.fulfilled, (state, action) => {
        state.suggestedLabels = action.payload
      })

      // 建立標籤
      .addCase(createLabel.fulfilled, (state, action) => {
        state.allLabels.unshift(action.payload)
      })
      .addCase(createLabel.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create label'
      })

      // 更新標籤
      .addCase(updateLabel.fulfilled, (state, action) => {
        const index = state.allLabels.findIndex(label => label.id === action.payload.id)
        if (index !== -1) {
          state.allLabels[index] = action.payload
        }
      })
      .addCase(updateLabel.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update label'
      })

      // 刪除標籤
      .addCase(deleteLabel.fulfilled, (state, action) => {
        state.allLabels = state.allLabels.filter(label => label.id !== action.payload)
        state.recentLabels = state.recentLabels.filter(label => label.id !== action.payload)
        state.suggestedLabels = state.suggestedLabels.filter(label => label.id !== action.payload)
      })
      .addCase(deleteLabel.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete label'
      })

      // 更新議題標籤
      .addCase(updateIssueLabels.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update issue labels'
      })
  }
})

export const { setSearchQuery, clearError, updateRecentLabels } = labelSlice.actions
export default labelSlice.reducer